import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Box,
  Button,
  Card,
  Divider,
  Fade,
  Flex,
  Heading,
  Icon,
  Link,
  List,
  ListIcon,
  ListItem,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRightIcon, CheckIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { FormTelephoneInput } from '~/components/form-telephone-input';
import { FormTextInput } from '~/components/form-text-input';
import { Glyph } from '~/components/glyph';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { phoneNumberSchema } from '~/features/phone-number/models';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

export type NewsletterSectionProps = {
  title: string;
  features: string[];
};

export function NewsletterSection({ title, features }: NewsletterSectionProps) {
  return (
    <Box
      as='section'
      bgGradient='linear(180deg, #F0F5FF 0%, #FFFFFF 60.73%)'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer
        display='flex'
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: 8,
          md: '80px',
        }}
      >
        <Flex flexDirection='column' gap={8} justifyContent='center' position='relative'>
          <HeadingWithEmphasis
            text={title}
            emphasisProps={{
              background: 'linear-gradient(89.43deg, #62BBFF 73.65%, #006AFF 97.67%)',
              backgroundClip: 'text',
              sx: {
                backgroundClip: 'text',
                textFillColor: 'transparent',
              },
            }}
            headingProps={{
              as: 'h1',
              fontWeight: '500 !important',
              size: {
                base: 'marketing-3xl',
                md: 'marketing-4xl',
              },
              whiteSpace: 'pre-wrap',
              color: 'black',
            }}
          />
          <Divider as='div' borderColor='#6491FC33' />
          <Box>
            <Heading
              as='p'
              mb={4}
              size={{
                base: 'marketing-xl',
                md: 'marketing-2xl',
              }}
            >
              Sign up to:
            </Heading>
            <List
              display='flex'
              flexDirection='column'
              gap={{
                base: 2,
                md: 4,
              }}
            >
              {features.map((feature) => (
                <ListItem key={feature} display='flex'>
                  <ListIcon as={CheckIcon} color='accent.sky.500' height='24px' width='24px' />
                  <Heading
                    as='p'
                    fontFamily={{
                      base: 'body !important',
                      md: 'heading',
                    }}
                    size={{
                      base: 'marketing-md',
                      md: 'marketing-lg',
                    }}
                  >
                    {feature}
                  </Heading>
                </ListItem>
              ))}
            </List>
          </Box>
        </Flex>
        <Card
          position='relative'
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            padding: '1px',
            background: 'linear-gradient(90deg, #62BBFF 0%, #006AFF 100%)',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          }}
          flex={{
            md: '0 1 500px',
          }}
          padding={{
            base: '24px',
            md: '40px 48px',
          }}
        >
          <NewsletterForm />
        </Card>
      </ContentContainer>
    </Box>
  );
}

const formDataSchema = z
  .object({
    email: z.string().email(),
    phone: phoneNumberSchema.optional(),
  })
  .refine(
    (data) => {
      return !!data.email || !!data.phone;
    },
    {
      message: 'Please provide either an email or phone number',
      path: ['email', 'phone'],
    }
  );

type FormData = z.infer<typeof formDataSchema>;

function NewsletterForm() {
  const toast = useToast();
  const [formState, setFormState] = useState<'success' | 'idle'>('idle');
  const { isSubmitting: isSubmittingSegmentEvents, sendSegmentLeadFormEvents } = useLeadFormEvent();
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    disabled: isSubmittingSegmentEvents || formState === 'success',
  });

  const submitButtonLabel = 'Sign up now' as const;

  async function submit(formData: FormData) {
    const response = await sendSegmentLeadFormEvents({
      identifyTraits: {
        email: formData.email,
        // Note: phone is a reserved field in Segment in the identify event.
        phone: formData.phone,
        // Note: not sure why but this is required for downstream services.
        phone_number: formData.phone,
      },
      trackAnalyticsEvent: {
        name: 'Marketing Qualified Lead Form Submitted',
        type: 'submit',
        label: 'Lead Generation - Agentic LP',
        action: 'form_submitted',
        additionalAttributes: transformFormDataToEventProperties(
          formData.phone
            ? {
                email: formData.email,
                // Note: Downstream services expect phone_number instead of phone from this track event.
                phone_number: formData.phone,
              }
            : {
                email: formData.email,
              }
        ),
      },
      integrations: {
        sendToSalesforce: true,
      },
    });

    if (response.success) {
      setFormState('success');
    } else {
      toast({
        title: '🤔 Something went wrong.',
        description: 'An error occurred submitting the form. Please try again.',
        variant: 'error',
      });
      setFormState('idle');
    }
  }

  return (
    <>
      <Flex
        as='form'
        flexDir='column'
        gap={{
          base: 4,
          md: 6,
        }}
        onSubmit={handleSubmit(submit)}
      >
        <Flex
          flexDir='column'
          gap={{
            base: 4,
            md: 6,
          }}
        >
          <FormTextInput
            label='Email*'
            {...register('email')}
            errorMessage={errors.email?.message}
            placeholder='Your Email'
            type='email'
          />
          <FormTelephoneInput
            control={control}
            errorMessage={errors.phone?.message}
            isDisabled={isSubmitting}
            label='Phone Number'
            name='phone'
          />
        </Flex>
        <Button
          isDisabled={isSubmitting || isSubmittingSegmentEvents}
          isLoading={isSubmitting || isSubmittingSegmentEvents}
          margin='0 auto'
          maxWidth='208px'
          rightIcon={<Icon as={ArrowRightIcon} />}
          type='submit'
          width='full'
        >
          {submitButtonLabel}
        </Button>

        <Text as='i' fontSize='sm'>
          By clicking &quot;{submitButtonLabel}&quot;, you agree to receive marketing communications from Udacity by
          email, text, and phone call and you agree to Udacity&apos;s{' '}
          <Box as={Link} fontSize='sm' href={`/legal/terms-of-use`} textDecoration='underline'>
            Terms of Use
          </Box>{' '}
          and{' '}
          <Box as={Link} fontSize='sm' href={`/legal/privacy`} textDecoration='underline'>
            Privacy Policy
          </Box>
          . Call and message rates may apply.
        </Text>
      </Flex>
      <Flex
        alignItems='center'
        aria-hidden={formState !== 'success'}
        as={Fade}
        bgColor='white'
        flexDirection='column'
        gap={6}
        in={formState === 'success'}
        inset={0}
        justifyContent='center'
        position='absolute'
        textAlign='center'
        padding={{
          base: '24px 16px',
          md: '32px 48px',
        }}
        unmountOnExit
      >
        <Box>
          <Glyph height={64} name='checkmark-medal-two' width={64} />
        </Box>
        <Heading
          as='p'
          whiteSpace='pre-line'
          size={{
            base: 'marketing-xl',
            md: 'marketing-2xl',
          }}
        >
          {`You're signed up to
receive updates!`}
        </Heading>
      </Flex>
    </>
  );
}
